import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import useTheme from '@material-ui/core/styles/useTheme'
import Accordion from '@material-ui/core/Accordion'
import AccordionSummary from '@material-ui/core/AccordionSummary'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import NoSsr from '@material-ui/core/NoSsr'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import LocationOnIcon from '@material-ui/icons/LocationOnOutlined'
import LinkIcon from '@material-ui/icons/Link'
import Link from '@components/link'
import { path, or } from 'ramda'
import { useCoachProfileInColumn } from '@lib/pga-coach/coach-profile-layout'
import {
  COACH_PROFILE_META_ICON_PROPS,
  ExternalLink,
  MapPin,
  Users,
} from '@lib/pga-coach/coach-profile-icons'
import { buildFacilityMapsHref } from '@components/coach-bio/coach-bio'
import { useTrackCoachProfileEvent } from '@lib/pga-coach/coach-profile-analytics'
import {
  FacilitiesWrapper,
  OfferingsWrapper,
  Offering,
  useStyles,
  PROFILE_FACILITY_ACCORDION_SUMMARY_MIN_HEIGHT,
} from './coach-facilities.styles'
import CoachFacilityMap from './coach-facility-map'

const FACILITIES_HEADING_ID = 'coach-facilities-heading'

function facilityShowsPublicCoachingNote(facility) {
  const offersCoachingAtPrivateClub = path(['customData', 'offersCoachingAtPrivateClub'], facility)
  return or(!facility.isPrivate, offersCoachingAtPrivateClub)
}

function PublicCoachingNote({ classes, profileUx, variant = 'body' }) {
  if (variant === 'summary') {
    return (
      <Box className={classes.publicCoachingNoteSummary} role="note">
        <Typography component="span" variant="caption">
          Coaching available to public
        </Typography>
      </Box>
    )
  }

  return (
    <Box className={classes.publicCoachingNote} role="note">
      <InfoOutlinedIcon className={classes.publicCoachingNoteIcon} aria-hidden />
      <Typography component="span" variant="body2" className={classes.publicCoachingNoteText}>
        Coaching available to public
      </Typography>
    </Box>
  )
}

function FacilityContent({
  facility,
  classes,
  profileUx,
  showPublicCoachingNoteInBody = true,
  showPublicCoachingMetaInProfile = false,
}) {
  const offerings = path(['customData', 'offerings'], facility)
  const description = path(['customData', 'description'], facility)
  const isCoachingAvailable = facilityShowsPublicCoachingNote(facility)
  const fullAddress = `${facility.street}, ${facility.city}, ${facility.state} ${facility.zipCode}`
  const mapsHref = profileUx ? buildFacilityMapsHref(facility) : null
  const trackCoachProfileEvent = useTrackCoachProfileEvent()
  const trackFacilityClick = (clickTarget) => {
    trackCoachProfileEvent('click-coach-profile-facility', {
      click_target: clickTarget,
      facility_id: facility.facilityId || null,
      facility_name: facility.facilityName || null,
    })
  }

  return (
    <>
      {
        facility.customData && facility.customData.photos && facility.customData.photos.length && facility.customData.photos[0] ? (
          <img
            src={facility.customData.photos[0]}
            alt={`${facility.facilityName}`}
            className={classes.facilityPhoto}
          />
        ) : null
      }
      {
        description ? (
          <Typography variant="body2" component="p" className={classes.facilityDescription}>{description}</Typography>
        ) : null
      }
      {
        isCoachingAvailable && showPublicCoachingNoteInBody && !profileUx && (
          <PublicCoachingNote classes={classes} profileUx={profileUx} />
        )
      }
      {
        profileUx && isCoachingAvailable && showPublicCoachingMetaInProfile && (
          <Box className={classes.publicCoachingNoteMetaRow} role="note">
            <Users {...COACH_PROFILE_META_ICON_PROPS} aria-hidden />
            <span className={classes.publicCoachingNoteMetaText}>
              Coaching available to public
            </span>
          </Box>
        )
      }
      <Box className={classes.detailRow}>
        {profileUx ? (
          <>
            <MapPin {...COACH_PROFILE_META_ICON_PROPS} aria-hidden />
            {mapsHref ? (
              <a
                href={mapsHref}
                onClick={() => trackFacilityClick('maps')}
                className={classes.facilityMetaLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open maps for ${fullAddress}`}
              >
                {fullAddress}
              </a>
            ) : (
              <Typography
                variant="subtitle2"
                component="span"
                className={classes.facilityMetaText}
              >
                {fullAddress}
              </Typography>
            )}
          </>
        ) : (
          <>
            <Box className={classes.iconWell} aria-hidden>
              <LocationOnIcon />
            </Box>
            <Typography variant="body1" component="p" className={classes.detailText}>
              {fullAddress}
            </Typography>
          </>
        )}
      </Box>
      {facility.website && (
        <Box className={profileUx ? classes.websiteRow : classes.detailRow}>
          {profileUx ? (
            <>
              <ExternalLink {...COACH_PROFILE_META_ICON_PROPS} aria-hidden />
              <a
                href={facility.website}
                onClick={() => trackFacilityClick('website')}
                className={classes.facilityMetaLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {facility.facilityName}
              </a>
            </>
          ) : (
            <>
              <Box className={classes.iconWell} aria-hidden>
                <LinkIcon fontSize="small" />
              </Box>
              <Typography variant="body1" component="p" className={classes.detailText}>
                <Link externalLink={facility.website} className={classes.link}>
                  {facility?.facilityName}
                </Link>
              </Typography>
            </>
          )}
        </Box>
      )}
      <NoSsr>
        <CoachFacilityMap
          facilityName={facility.facilityName}
          mapSearchAddress={`${facility.street}, ${facility.city}, ${facility.state} ${facility.zipCode}`}
          classes={classes}
          profilePillActions={profileUx}
        />
      </NoSsr>
      {
        offerings?.length ? (
          <Box>
            <Typography variant="overline" component="p" className={classes.offeringsSectionLabel}>Facility Offerings</Typography>
            <OfferingsWrapper>
              {
                offerings.map((offering) => (
                  <Offering key={offering.name}>
                    <Typography variant="h6" component="p" className={classes.offeringTitle}>{offering.name}</Typography>
                    <Typography variant="body2" component="p" className={classes.offeringDescription}>{offering.description}</Typography>
                  </Offering>
                ))
              }
            </OfferingsWrapper>
          </Box>
        ) : null
      }
    </>
  )
}

FacilityContent.propTypes = {
  classes: PropTypes.object.isRequired,
  profileUx: PropTypes.bool,
  showPublicCoachingNoteInBody: PropTypes.bool,
  showPublicCoachingMetaInProfile: PropTypes.bool,
  facility: PropTypes.shape({
    facilityName: PropTypes.string,
    city: PropTypes.string,
    description: PropTypes.string,
    offeringsJsonData: PropTypes.array,
    photosJsonData: PropTypes.array,
    state: PropTypes.string,
    street: PropTypes.string,
    isPrivate: PropTypes.bool,
    zipCode: PropTypes.string,
    website: PropTypes.string,
  }),
}

/**
 * Stable DOM id for the wrapper of a given facility entry. The hero
 * location line on the coach profile uses this to scroll-into-view
 * (and, in the multi-facility case, programmatically expand the
 * matching accordion via its `AccordionSummary`).
 */
export const coachFacilityAnchorId = (facilityId) => (
  facilityId ? `coach-facility-${facilityId}` : undefined
)

function FacilityContainer({ facilities, classes, profileUx }) {
  const theme = useTheme()
  const showPublicCoachingInAccordionSummary = useMediaQuery(theme.breakpoints.up('md'))
  const trackCoachProfileEvent = useTrackCoachProfileEvent()
  const [expandedFacilityId, setExpandedFacilityId] = useState(
    () => facilities[0]?.facilityId ?? false,
  )

  const handleAccordionChange = (facilityId) => (event, isExpanded) => {
    const facility = facilities.find((item) => item.facilityId === facilityId)
    trackCoachProfileEvent('click-coach-profile-facility', {
      click_target: isExpanded ? 'expand' : 'collapse',
      facility_id: facilityId || null,
      facility_name: facility?.facilityName || null,
    })
    setExpandedFacilityId(isExpanded ? facilityId : false)
  }

  if (facilities.length <= 1) {
    return (
      <Box
        id={coachFacilityAnchorId(facilities[0].facilityId)}
        className={classes.singleFacilityCard}
      >
        <Typography variant="h6" component="h3" className={classes.facilityHeadline}>{facilities[0].facilityName}</Typography>
        <FacilityContent facility={facilities[0]} classes={classes} profileUx={profileUx} />
      </Box>
    )
  }

  const accordionItems = facilities.map((facility, i) => {
    const isExpanded = expandedFacilityId === facility.facilityId
    return (
      <Accordion
        key={facility.facilityId}
        id={coachFacilityAnchorId(facility.facilityId)}
        expanded={isExpanded}
        onChange={handleAccordionChange(facility.facilityId)}
        className={classes.facilityAccordion}
        elevation={0}
        square
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
          className={classes.accordionSummary}
          classes={
            profileUx
              ? {
                expandIcon: classes.accordionExpandIcon,
              }
              : undefined
          }
          style={
            profileUx
              ? { minHeight: PROFILE_FACILITY_ACCORDION_SUMMARY_MIN_HEIGHT }
              : undefined
          }
          aria-controls={`panel${i}bh-content`}
          id={`panel${i}bh-header`}
        >
          <Box className={profileUx ? classes.accordionSummaryMain : undefined}>
            <Typography component="span" className={classes.accordionSummaryTitle}>{facility.facilityName}</Typography>
            {profileUx
              && facilityShowsPublicCoachingNote(facility)
              && showPublicCoachingInAccordionSummary && (
              <PublicCoachingNote classes={classes} profileUx={profileUx} variant="summary" />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          <FacilityContent
            facility={facility}
            classes={classes}
            profileUx={profileUx}
            showPublicCoachingNoteInBody={!profileUx}
            showPublicCoachingMetaInProfile={
              profileUx && !showPublicCoachingInAccordionSummary
            }
          />
        </AccordionDetails>
      </Accordion>
    )
  })

  if (profileUx) {
    return <Box className={classes.facilitiesAccordionList}>{accordionItems}</Box>
  }

  return accordionItems
}

FacilityContainer.propTypes = {
  classes: PropTypes.object.isRequired,
  facilities: PropTypes.arrayOf(PropTypes.shape({
    facilityName: PropTypes.string,
  })),
}

function CoachFacilities(props) {
  const { facilities, coach, leadsCallout } = props
  const inColumn = useCoachProfileInColumn()
  const classes = useStyles({ inColumn })
  const { firstName } = coach
  const hasFacilities = facilities && facilities.length > 0
  const sectionTitle = inColumn
    ? (facilities.length > 1 ? 'Locations' : 'Location')
    : `${firstName}'s Facilities`

  if (!hasFacilities && !leadsCallout) return null

  return (
    <Box
      component="section"
      className={classes.root}
      aria-labelledby={hasFacilities ? FACILITIES_HEADING_ID : undefined}
      data-testid={inColumn ? 'coach-profile-facilities' : undefined}
    >
      <Box className={classes.insetContainer}>
        {hasFacilities
          ? (
            <FacilitiesWrapper>
              <Typography
                id={FACILITIES_HEADING_ID}
                component="h2"
                variant={inColumn ? 'h5' : 'h4'}
                className={classes.sectionHeading}
              >
                {sectionTitle}
              </Typography>
              <FacilityContainer facilities={facilities} classes={classes} profileUx={inColumn} />
            </FacilitiesWrapper>
          )
          : null}
        {leadsCallout}
      </Box>
    </Box>
  )
}

CoachFacilities.propTypes = {
  facilities: PropTypes.array,
  coach: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    displayName: PropTypes.string,
    coachProfileId: PropTypes.string,
    coach: PropTypes.shape({
      preferences: PropTypes.shape({
        acceptsLeads: PropTypes.bool.isRequired,
      }),
      freeTierLimitsReached: PropTypes.array,
    }).isRequired,
  }),
  leadsCallout: PropTypes.node,
}

CoachFacilities.defaultProps = {
  leadsCallout: null,
}

export default CoachFacilities
